/**
 * HYPSEYA Icon System
 * Unique SVG icons designed exclusively for the HYPSEYA brand.
 * Thin-stroke, organic, minimal — nothing generic.
 */

const defaults = { size: 20, color: 'currentColor', strokeWidth: 1.5 };

// ── Spark ─────────────────────────────────────────────────────────────────
// Replaces ✨  — a radiant burst: 4 long rays + 4 short rays
export function Spark({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="10" y1="2"  x2="10" y2="5"  stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="10" y1="15" x2="10" y2="18" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="2"  y1="10" x2="5"  y2="10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="15" y1="10" x2="18" y2="10" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="4.1"  y1="4.1"  x2="6.2"  y2="6.2"  stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="13.8" y1="13.8" x2="15.9" y2="15.9" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="15.9" y1="4.1"  x2="13.8" y2="6.2"  stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="6.2"  y1="13.8" x2="4.1"  y2="15.9" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <circle cx="10" cy="10" r="2" fill={color}/>
    </svg>
  );
}

// ── Leaf ──────────────────────────────────────────────────────────────────
// Replaces 🌿  — a single curved leaf with central vein
export function Leaf({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 17 C10 17 3 14 3 7 C3 4 6 2 10 2 C14 2 17 4 17 7 C17 14 10 17 10 17Z"
        stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
        fill="none"
      />
      <path d="M10 17 L10 6" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M10 11 Q7.5 9 6 7"   stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
      <path d="M10 9  Q12.5 7 14 5.5" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
    </svg>
  );
}

// ── Mic ───────────────────────────────────────────────────────────────────
// Replaces 🎙️  — capsule body, curved stand, base line
export function Mic({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="2" width="6" height="10" rx="3" stroke={color} strokeWidth="1.5"/>
      <path d="M4.5 10 C4.5 14 15.5 14 15.5 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <line x1="10" y1="14" x2="10" y2="17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="7"  y1="17" x2="13" y2="17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Headphones ────────────────────────────────────────────────────────────
// Replaces 🎧  — arc + two ear cups
export function Headphones({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.5 11 C3.5 6.3 6.4 3 10 3 C13.6 3 16.5 6.3 16.5 11"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <rect x="2"  y="11" width="4" height="5.5" rx="1.5" stroke={color} strokeWidth="1.4"/>
      <rect x="14" y="11" width="4" height="5.5" rx="1.5" stroke={color} strokeWidth="1.4"/>
    </svg>
  );
}

// ── Chart ─────────────────────────────────────────────────────────────────
// Replaces 📊  — three ascending bars + baseline
export function Chart({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2.5"  y="13" width="4" height="4" rx="1" stroke={color} strokeWidth="1.4"/>
      <rect x="8"    y="9"  width="4" height="8" rx="1" stroke={color} strokeWidth="1.4"/>
      <rect x="13.5" y="5"  width="4" height="12" rx="1" stroke={color} strokeWidth="1.4"/>
      <line x1="1.5" y1="17.5" x2="18.5" y2="17.5" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

// ── Save ──────────────────────────────────────────────────────────────────
// Replaces 💾  — a downward arrow landing into a tray
export function Save({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="10" y1="3" x2="10" y2="13" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <polyline points="6.5,9.5 10,13.5 13.5,9.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M3.5 15 L3.5 17 L16.5 17 L16.5 15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── Cycle ─────────────────────────────────────────────────────────────────
// Replaces 🔄  — circular arrow (regenerate)
export function Cycle({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.5 10 A6.5 6.5 0 1 1 13.5 4.5"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      <polyline points="11,2 14,5 11,8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── Meditate ──────────────────────────────────────────────────────────────
// Replaces 🧘  — abstract seated form + breath arc above
export function Meditate({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="4" r="2" stroke={color} strokeWidth="1.4"/>
      <path d="M7 8 Q10 7 13 8 L14 13 Q10 15 6 13 Z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
      <path d="M6 13 Q4.5 14.5 5 16" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none"/>
      <path d="M14 13 Q15.5 14.5 15 16" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────
// Replaces ⚠️  — diamond with inner dot, no generic triangle
export function Alert({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 2.5 L17.5 10 L10 17.5 L2.5 10 Z"
        stroke={color} strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
      <line x1="10" y1="8" x2="10" y2="11.5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="10" cy="13.5" r="0.9" fill={color}/>
    </svg>
  );
}

// ── Folder ────────────────────────────────────────────────────────────────
// Replaces 📁  — minimal folder tab shape
export function Folder({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 6 L2 16 Q2 17.5 3.5 17.5 L16.5 17.5 Q18 17.5 18 16 L18 8 Q18 6.5 16.5 6.5 L10.5 6.5 L9 4.5 Q8.5 3.5 7.5 3.5 L3.5 3.5 Q2 3.5 2 5 Z"
        stroke={color} strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── User ──────────────────────────────────────────────────────────────────
// Replaces 👤  — circle head + shoulders arc
export function User({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="7" r="3.5" stroke={color} strokeWidth="1.4"/>
      <path d="M3 18 C3 14.5 6 12 10 12 C14 12 17 14.5 17 18"
        stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

// ── Wave ──────────────────────────────────────────────────────────────────
// For calm/regulation states
export function Wave({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 10 C4 7 6 13 8 10 C10 7 12 13 14 10 C16 7 18 13 18 10"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

// ── Check ─────────────────────────────────────────────────────────────────
// Replaces ✓  — thin elegant checkmark
export function Check({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polyline points="4,11 8.5,15.5 16,6"
        stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── ArrowRight ────────────────────────────────────────────────────────────
export function ArrowRight({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="3" y1="10" x2="16" y2="10" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <polyline points="11,5.5 16,10 11,14.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── ArrowLeft ─────────────────────────────────────────────────────────────
export function ArrowLeft({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="17" y1="10" x2="4" y2="10" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <polyline points="9,5.5 4,10 9,14.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── ArrowUp / ArrowDown ───────────────────────────────────────────────────
export function ArrowUp({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="10" y1="17" x2="10" y2="4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <polyline points="5.5,9 10,4 14.5,9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

export function ArrowDown({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="10" y1="3" x2="10" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <polyline points="5.5,11 10,16 14.5,11" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ── Stable (→ horizontal line with small notches) ─────────────────────────
export function Stable({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="3" y1="10" x2="17" y2="10" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="3" y1="7.5" x2="3" y2="12.5" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="17" y1="7.5" x2="17" y2="12.5" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

// ── Program Icons ─────────────────────────────────────────────────────────
// One unique symbol per structured program. Thin-stroke, no emojis.

// GriefIcon — a single teardrop releasing upward into open space
export function GriefIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Teardrop body */}
      <path d="M10 16 C6.5 16 4 13.5 4 10.5 C4 7 10 3 10 3 C10 3 16 7 16 10.5 C16 13.5 13.5 16 10 16Z"
        stroke={color} strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
      {/* Inner vertical breath line */}
      <line x1="10" y1="7.5" x2="10" y2="12" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
      {/* Small release arc at top */}
      <path d="M8 3.5 Q10 1.5 12 3.5" stroke={color} strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.5"/>
    </svg>
  );
}

// AnxietyIcon — three concentric expanding arcs (nervous system activation waves)
export function AnxietyIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Centre dot */}
      <circle cx="10" cy="10" r="1.2" fill={color}/>
      {/* First arc */}
      <path d="M7 10 C7 8.3 8.3 7 10 7 C11.7 7 13 8.3 13 10"
        stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
      {/* Second arc */}
      <path d="M4.5 10 C4.5 6 6.9 3.5 10 3.5 C13.1 3.5 15.5 6 15.5 10"
        stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.65"/>
      {/* Third arc — outermost, faintest */}
      <path d="M2 10 C2 4 5.5 1.5 10 1.5 C14.5 1.5 18 4 18 10"
        stroke={color} strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.3"/>
    </svg>
  );
}

// BurnoutIcon — an ember: a flame reduced to its last flicker
export function BurnoutIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Outer flame shape — subdued */}
      <path d="M10 17 C6 17 4 14 4 11 C4 8 6 6 7.5 4 C7.5 6.5 9 8 9 8 C9 8 10.5 5 11 3 C13.5 5.5 16 8 16 11 C16 14 14 17 10 17Z"
        stroke={color} strokeWidth="1.4" strokeLinejoin="round" fill="none" opacity="0.7"/>
      {/* Inner ember — small core */}
      <path d="M10 15 C8.5 15 7.5 14 7.5 12.5 C7.5 11 8.5 10 9.5 9 C9.5 10 10.5 11 10.5 11 C11.5 10 12.5 9 12.5 12.5 C12.5 14 11.5 15 10 15Z"
        stroke={color} strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// IntegrationIcon — a broken circle healing: two arcs almost meeting
export function IntegrationIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left arc — the past */}
      <path d="M10 3 C6.1 3 3 6.1 3 10 C3 13.9 6.1 17 10 17"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      {/* Right arc — the present healing */}
      <path d="M10 17 C13.9 17 17 13.9 17 10 C17 6.1 13.9 3 10 3"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.45"/>
      {/* Bridge — the integration point */}
      <line x1="10" y1="3" x2="10" y2="6" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
      <line x1="10" y1="14" x2="10" y2="17" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
      {/* Centre dot */}
      <circle cx="10" cy="10" r="1.5" stroke={color} strokeWidth="1.2" fill="none"/>
    </svg>
  );
}

// RedesignIcon — an architectural grid: 4 nodes connected, one transforming
export function RedesignIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Old structure — three connected nodes */}
      <circle cx="4" cy="4" r="1.5" stroke={color} strokeWidth="1.2" fill="none" opacity="0.4"/>
      <circle cx="16" cy="4" r="1.5" stroke={color} strokeWidth="1.2" fill="none" opacity="0.4"/>
      <circle cx="4" cy="16" r="1.5" stroke={color} strokeWidth="1.2" fill="none" opacity="0.4"/>
      <line x1="4" y1="4" x2="16" y2="4" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      <line x1="4" y1="4" x2="4" y2="16" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      <line x1="4" y1="16" x2="16" y2="4" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      {/* New node — emerging, stronger */}
      <circle cx="16" cy="16" r="2.5" stroke={color} strokeWidth="1.5" fill="none"/>
      {/* Lines from new node — the redesign */}
      <line x1="13.5" y1="16" x2="4" y2="16" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="16" y1="13.5" x2="16" y2="4" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

// ── MoodGlyph ─────────────────────────────────────────────────────────────
// Replaces face emojis — abstract activation indicator (level 1–10)
// Low levels: calm small form. High levels: radiant expansive form.
export function MoodGlyph({ level = 5, size = 32, color = '#1E6B68', isSelected = false } = {}) {
  const c = 16; // center
  const vb = 32; // viewBox size
  const t = isSelected ? color : color;
  const opacity = isSelected ? 1 : 0.55;

  // Inner circle: grows from r=2 at level 1 to r=8 at level 10
  const innerR = 2 + (level - 1) * 0.72;
  const outerR = innerR + 3.5;
  const showOuter = level >= 4;
  const showFarOuter = level >= 7;
  const showRays = level >= 8;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${vb} ${vb}`} fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ opacity }}>
      {/* Far outer ring */}
      {showFarOuter && (
        <circle cx={c} cy={c} r={outerR + 4}
          stroke={t} strokeWidth="0.8" opacity="0.3" fill="none"/>
      )}
      {/* Outer ring */}
      {showOuter && (
        <circle cx={c} cy={c} r={outerR}
          stroke={t} strokeWidth={isSelected ? 1.2 : 0.9} opacity="0.6" fill="none"/>
      )}
      {/* Rays at high activation */}
      {showRays && [0,45,90,135,180,225,270,315].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const r1 = outerR + 2;
        const r2 = outerR + 4.5;
        return (
          <line key={i}
            x1={c + r1 * Math.cos(rad)} y1={c + r1 * Math.sin(rad)}
            x2={c + r2 * Math.cos(rad)} y2={c + r2 * Math.sin(rad)}
            stroke={t} strokeWidth="1" strokeLinecap="round" opacity="0.7"/>
        );
      })}
      {/* Inner core */}
      <circle cx={c} cy={c} r={innerR}
        fill={isSelected ? t : 'none'}
        stroke={t} strokeWidth={isSelected ? 0 : 1.5}
      />
    </svg>
  );
}

// ── BodySenseIcon ──────────────────────────────────────────────────────────
// Replaces 🤲  — two cupped open hands, thin-stroke, somatic sensing gesture
export function BodySenseIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left palm — curved cup opening right */}
      <path d="M2 16.5 C2 13 3.5 10.5 6.5 9.5 L7 11 L7 16.5 Q4.5 17.5 2 16.5 Z"
        stroke={color} strokeWidth="1.35" strokeLinejoin="round" fill="none"/>
      {/* Left fingers — three soft arcs rising from top of left palm */}
      <path d="M3.5 10.5 Q3 8 4 7" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none"/>
      <path d="M5 9.5 Q5 7 5.5 6" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none"/>
      <path d="M6.5 9.5 Q7 7 7.5 6.5" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none"/>
      {/* Right palm — mirror of left */}
      <path d="M18 16.5 C18 13 16.5 10.5 13.5 9.5 L13 11 L13 16.5 Q15.5 17.5 18 16.5 Z"
        stroke={color} strokeWidth="1.35" strokeLinejoin="round" fill="none"/>
      {/* Right fingers */}
      <path d="M16.5 10.5 Q17 8 16 7" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none"/>
      <path d="M15 9.5 Q15 7 14.5 6" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none"/>
      <path d="M13.5 9.5 Q13 7 12.5 6.5" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none"/>
      {/* Centre: small pulse dot between the two hands */}
      <circle cx="10" cy="14" r="1.1" fill={color} opacity="0.35"/>
    </svg>
  );
}

// ── Bloom ─────────────────────────────────────────────────────────────────
// Replaces 🌸  — feminine guide icon: lotus with 6 soft petals + centre dot
export function Bloom({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 6 petals — each a small ellipse rotated around centre */}
      <ellipse cx="10" cy="6.2"  rx="1.6" ry="2.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" transform="rotate(0   10 10)"/>
      <ellipse cx="10" cy="6.2"  rx="1.6" ry="2.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" transform="rotate(60  10 10)"/>
      <ellipse cx="10" cy="6.2"  rx="1.6" ry="2.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" transform="rotate(120 10 10)"/>
      <ellipse cx="10" cy="6.2"  rx="1.6" ry="2.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" transform="rotate(180 10 10)"/>
      <ellipse cx="10" cy="6.2"  rx="1.6" ry="2.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" transform="rotate(240 10 10)"/>
      <ellipse cx="10" cy="6.2"  rx="1.6" ry="2.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none" transform="rotate(300 10 10)"/>
      {/* Centre dot */}
      <circle cx="10" cy="10" r="1.4" fill={color}/>
    </svg>
  );
}

// ── HeatIcon ───────────────────────────────────────────────────────────────
// Replaces 🌡  — minimal thermometer: bulb + column + two tick marks
export function HeatIcon({ size = defaults.size, color = defaults.color } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Bulb */}
      <circle cx="10" cy="15" r="2.5" stroke={color} strokeWidth="1.4" fill="none"/>
      {/* Column */}
      <path d="M8.8 14 L8.8 6 Q8.8 4.5 10 4.5 Q11.2 4.5 11.2 6 L11.2 14"
        stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
      {/* Two tick marks */}
      <line x1="11.2" y1="8"  x2="13" y2="8"  stroke={color} strokeWidth="1" strokeLinecap="round"/>
      <line x1="11.2" y1="10.5" x2="13" y2="10.5" stroke={color} strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}
